import React, { useRef } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Form,
  Label,
  Input,
  FormFeedback,
  Table,
  Button,
} from "reactstrap"
import toast from "toastr"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { saveWorkspaceAudit } from "helpers/backend_helper"
import TableContainer from "./table"

const GB = 1e9

const toBytes = gb => {
  const num = Number(gb)
  return isNaN(num) ? 0 : Math.round(num * GB)
}

const toNum = v => {
  const num = Number(v)
  return isNaN(num) ? 0 : num
}

const emptyFolder = { path: "", sizegb: "", filecount: "", directorycount: "" }
// Canonical category values must match the scan tool's FileCategory enum
// (SourceCode, Document, Image, Artifact, Other) so scanned and manual audits roll up together.
const CATEGORY_LABELS = {
  SourceCode: "Source Code",
  Document: "Documents",
  Artifact: "Build / Dev Artifacts",
  Image: "Images",
  Other: "Other / Unknown",
}
const categoryLabel = value => CATEGORY_LABELS[value] || value
const emptyCategory = category => ({
  category,
  filecount: "",
  sizegb: "",
  percentage: "",
})
const emptyRepository = {
  name: "",
  rootpath: "",
  branch: "",
  remoteurl: "",
  sizegb: "",
  filecount: "",
  isclean: true,
  isinsideonedrive: false,
  lastcommitsummary: "",
}
const emptyOneDriveLocation = {
  rootpath: "",
  account: "",
  sizegb: "",
  filecount: "",
  hasgitrepos: false,
  contenttypes: "",
}
const emptyLargestFolder = { path: "", sizegb: "" }
const emptyLargestFile = { path: "", sizegb: "", filetype: "" }
const emptyArtifactFolder = { path: "", sizegb: "", filecount: "", directorycount: "" }

const ARTIFACT_TYPE_OPTIONS = [
  "bin",
  "obj",
  ".vs",
  "node_modules",
  "packages",
  "dist",
  "build",
  "publish",
  "TestResults",
  "caches",
]
const ONEDRIVE_ACCOUNT_OPTIONS = ["Personal", "Business"]

const Index = () => {
  document.title = "Workspace Audit | TenderSoftware - Flexi TracBoard"
  const tableRef = useRef(null)

  const validation = useFormik({
    enableReinitialize: false,
    initialValues: {
      // Section A
      machinename: "",
      username: "",
      osdescription: "",
      localdrivecount: "",
      totaldiskcapacitygb: "",
      totalfreediskgb: "",
      assessmentdate: "",
      // Section B
      folders: [{ ...emptyFolder }],
      // Section C
      totalfiles: "",
      totaldirectories: "",
      totalsizegb: "",
      categories: [
        emptyCategory("SourceCode"),
        emptyCategory("Document"),
        emptyCategory("Artifact"),
        emptyCategory("Image"),
        emptyCategory("Other"),
      ],
      // Section D
      repositorycount: "",
      repositories: [{ ...emptyRepository }],
      // Section E
      controlledfilecount: "",
      controlledsizegb: "",
      uncontrolledfilecount: "",
      uncontrolledsizegb: "",
      controlledpercentage: "",
      notableuncontrolledareas: "",
      // Section F
      documentfilecount: "",
      documentsizegb: "",
      documentlocations: "",
      documentsoutsidegit: "",
      // Section G
      artifactsizegb: "",
      artifacttypes: [],
      artifactfolders: [{ ...emptyArtifactFolder }],
      // Section H
      onedrivesynced: "",
      onedriveaccounttypes: [],
      onedrivelocations: [{ ...emptyOneDriveLocation }],
      onedrivegitrepos: "",
      // Section I
      largestfolders: [{ ...emptyLargestFolder }],
      largestfiles: [{ ...emptyLargestFile }],
      // Section J
      limitingnotes: "",
      storageconcerns: "",
    },
    validationSchema: Yup.object({
      machinename: Yup.string().required("Please enter the machine name"),
      osdescription: Yup.string().required("Please enter the operating system"),
      localdrivecount: Yup.number()
        .typeError("Must be a number")
        .required("Please enter the number of local drives"),
      assessmentdate: Yup.string().required(
        "Please select the assessment date"
      ),
      repositorycount: Yup.number()
        .typeError("Must be a number")
        .required("Please enter the repository count"),
      onedrivesynced: Yup.string().required(
        "Please select whether OneDrive is synced"
      ),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const onedrivelocations = (values.onedrivelocations || [])
          .filter(r => r.rootpath)
          .map(r => ({
            rootpath: r.rootpath,
            account: r.account,
            sizebytes: toBytes(r.sizegb),
            filecount: toNum(r.filecount),
            hasgitrepos: !!r.hasgitrepos,
            contenttypes: r.contenttypes,
          }))

        const folders = [
          ...(values.folders || [])
            .filter(f => f.path)
            .map(f => ({
              path: f.path,
              sizebytes: toBytes(f.sizegb),
              filecount: toNum(f.filecount),
              directorycount: toNum(f.directorycount),
              foldertype: "analysed",
            })),
          ...(values.largestfolders || [])
            .filter(f => f.path)
            .map(f => ({
              path: f.path,
              sizebytes: toBytes(f.sizegb),
              filecount: 0,
              directorycount: 0,
              foldertype: "largest",
            })),
          ...(values.artifactfolders || [])
            .filter(f => f.path)
            .map(f => ({
              path: f.path,
              sizebytes: toBytes(f.sizegb),
              filecount: toNum(f.filecount),
              directorycount: toNum(f.directorycount),
              foldertype: "artifact",
            })),
        ]

        const payload = {
          source: "manual",
          machinename: values.machinename,
          username: values.username,
          osdescription: values.osdescription,
          assessmentdate: values.assessmentdate,
          scandurationseconds: null,
          localdrivecount: toNum(values.localdrivecount),
          totaldiskcapacitybytes: toBytes(values.totaldiskcapacitygb),
          totalfreediskbytes: toBytes(values.totalfreediskgb),
          totalfiles: toNum(values.totalfiles),
          totaldirectories: toNum(values.totaldirectories),
          totalsizebytes: toBytes(values.totalsizegb),
          repositorycount: toNum(values.repositorycount),
          controlledfilecount: toNum(values.controlledfilecount),
          controlledsizebytes: toBytes(values.controlledsizegb),
          uncontrolledfilecount: toNum(values.uncontrolledfilecount),
          uncontrolledsizebytes: toBytes(values.uncontrolledsizegb),
          controlledpercentage: toNum(values.controlledpercentage),
          documentfilecount: toNum(values.documentfilecount),
          documentsizebytes: toBytes(values.documentsizegb),
          documentlocations: values.documentlocations,
          documentsoutsidegit: values.documentsoutsidegit,
          artifactsizebytes: toBytes(values.artifactsizegb),
          artifacttypes: (values.artifacttypes || []).join(","),
          onedrivesynced: values.onedrivesynced,
          onedriveaccounttypes: (values.onedriveaccounttypes || []).join(","),
          onedrivefilecount: onedrivelocations.reduce(
            (sum, r) => sum + (r.filecount || 0),
            0
          ),
          onedrivesizebytes: onedrivelocations.reduce(
            (sum, r) => sum + (r.sizebytes || 0),
            0
          ),
          onedrivegitrepos: values.onedrivegitrepos,
          notableuncontrolledareas: values.notableuncontrolledareas,
          limitingnotes: values.limitingnotes,
          storageconcerns: values.storageconcerns,
          disksjson: null,
          extensionsjson: null,
          categories: (values.categories || []).map(c => ({
            category: c.category,
            filecount: toNum(c.filecount),
            sizebytes: toBytes(c.sizegb),
            percentage: toNum(c.percentage),
          })),
          repositories: (values.repositories || [])
            .filter(r => r.name || r.rootpath)
            .map(r => ({
              name: r.name,
              rootpath: r.rootpath,
              branch: r.branch,
              remoteurl: r.remoteurl,
              sizebytes: toBytes(r.sizegb),
              filecount: toNum(r.filecount),
              isclean: !!r.isclean,
              isinsideonedrive: !!r.isinsideonedrive,
              lastcommitsummary: r.lastcommitsummary,
            })),
          onedrivelocations,
          folders,
          largestfiles: (values.largestfiles || [])
            .filter(f => f.path)
            .map(f => ({
              path: f.path,
              sizebytes: toBytes(f.sizegb),
              filetype: f.filetype,
            })),
        }

        const res = await saveWorkspaceAudit(payload)
        if (res?.statusCode === 200 || res?.StatusCode === 200) {
          toast.success("Workspace audit saved successfully")
          resetForm()
          tableRef.current?.refresh()
        } else {
          toast.error(res?.message || res?.Message || "Failed to save workspace audit")
        }
      } catch (error) {
        toast.error(error?.message || "Failed to save workspace audit")
      }
    },
  })

  const addRow = (field, emptyRow) => {
    validation.setFieldValue(field, [...(validation.values[field] || []), emptyRow])
  }

  const removeRow = (field, idx) => {
    const rows = [...(validation.values[field] || [])]
    rows.splice(idx, 1)
    validation.setFieldValue(field, rows)
  }

  const updateRow = (field, idx, key, value) => {
    const rows = [...(validation.values[field] || [])]
    rows[idx] = { ...rows[idx], [key]: value }
    validation.setFieldValue(field, rows)
  }

  const toggleCheckbox = (field, value) => {
    const current = validation.values[field] || []
    if (current.includes(value)) {
      validation.setFieldValue(
        field,
        current.filter(v => v !== value)
      )
    } else {
      validation.setFieldValue(field, [...current, value])
    }
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title={"Workspace Audit"} breadcrumbItem={"Workspace Audit"} />

        <Card>
          <CardBody>
            <Form
              onSubmit={e => {
                e.preventDefault()
                validation.handleSubmit()
                return false
              }}
            >
              {/* SECTION A - Machine Overview */}
              <h5 className="mb-3">A. Machine Overview</h5>
              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Computer / host name *</Label>
                    <Input
                      name="machinename"
                      type="text"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.machinename}
                      invalid={!!(validation.touched.machinename && validation.errors.machinename)}
                    />
                    {validation.touched.machinename && validation.errors.machinename ? (
                      <FormFeedback type="invalid">{validation.errors.machinename}</FormFeedback>
                    ) : null}
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Your username</Label>
                    <Input
                      name="username"
                      type="text"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.username}
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Operating system *</Label>
                    <Input
                      name="osdescription"
                      type="select"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.osdescription}
                      invalid={!!(validation.touched.osdescription && validation.errors.osdescription)}
                    >
                      <option value="">Select...</option>
                      <option value="Windows 11">Windows 11</option>
                      <option value="Windows 10">Windows 10</option>
                      <option value="macOS">macOS</option>
                      <option value="Linux">Linux</option>
                      <option value="Other">Other</option>
                    </Input>
                    {validation.touched.osdescription && validation.errors.osdescription ? (
                      <FormFeedback type="invalid">{validation.errors.osdescription}</FormFeedback>
                    ) : null}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Number of local drives *</Label>
                    <Input
                      name="localdrivecount"
                      type="number"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.localdrivecount}
                      invalid={!!(validation.touched.localdrivecount && validation.errors.localdrivecount)}
                    />
                    {validation.touched.localdrivecount && validation.errors.localdrivecount ? (
                      <FormFeedback type="invalid">{validation.errors.localdrivecount}</FormFeedback>
                    ) : null}
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Total disk capacity (GB)</Label>
                    <Input
                      name="totaldiskcapacitygb"
                      type="number"
                      onChange={validation.handleChange}
                      value={validation.values.totaldiskcapacitygb}
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Total free disk space (GB)</Label>
                    <Input
                      name="totalfreediskgb"
                      type="number"
                      onChange={validation.handleChange}
                      value={validation.values.totalfreediskgb}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Date of this assessment *</Label>
                    <Input
                      name="assessmentdate"
                      type="date"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.assessmentdate}
                      invalid={!!(validation.touched.assessmentdate && validation.errors.assessmentdate)}
                    />
                    {validation.touched.assessmentdate && validation.errors.assessmentdate ? (
                      <FormFeedback type="invalid">{validation.errors.assessmentdate}</FormFeedback>
                    ) : null}
                  </div>
                </Col>
              </Row>

              <hr />

              {/* SECTION B - Folders Analysed */}
              <h5 className="mb-3">B. Folders Analysed</h5>
              <GridSection
                rows={validation.values.folders}
                columns={[
                  { key: "path", label: "Folder Path", type: "text" },
                  { key: "sizegb", label: "Approx. Size (GB)", type: "number" },
                  { key: "filecount", label: "Approx. File Count", type: "number" },
                  { key: "directorycount", label: "Folder Count", type: "number" },
                ]}
                onChange={(idx, key, value) => updateRow("folders", idx, key, value)}
                onAdd={() => addRow("folders", { ...emptyFolder })}
                onRemove={idx => removeRow("folders", idx)}
              />

              <hr />

              {/* SECTION C - Workspace Summary */}
              <h5 className="mb-3">C. Workspace Summary</h5>
              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Total files</Label>
                    <Input
                      name="totalfiles"
                      type="number"
                      onChange={validation.handleChange}
                      value={validation.values.totalfiles}
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Total folders</Label>
                    <Input
                      name="totaldirectories"
                      type="number"
                      onChange={validation.handleChange}
                      value={validation.values.totaldirectories}
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Total size (GB)</Label>
                    <Input
                      name="totalsizegb"
                      type="number"
                      onChange={validation.handleChange}
                      value={validation.values.totalsizegb}
                    />
                  </div>
                </Col>
              </Row>
              <Label>Storage split by category</Label>
              <div className="table-responsive mb-3">
                <Table bordered>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>File Count</th>
                      <th>Size (GB)</th>
                      <th>% of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validation.values.categories.map((row, idx) => (
                      <tr key={row.category}>
                        <td>{categoryLabel(row.category)}</td>
                        <td>
                          <Input
                            type="number"
                            value={row.filecount}
                            onChange={e => updateRow("categories", idx, "filecount", e.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            value={row.sizegb}
                            onChange={e => updateRow("categories", idx, "sizegb", e.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            value={row.percentage}
                            onChange={e => updateRow("categories", idx, "percentage", e.target.value)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <hr />

              {/* SECTION D - Git Repositories */}
              <h5 className="mb-3">D. Git Repositories</h5>
              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Number of Git repositories *</Label>
                    <Input
                      name="repositorycount"
                      type="number"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.repositorycount}
                      invalid={!!(validation.touched.repositorycount && validation.errors.repositorycount)}
                    />
                    {validation.touched.repositorycount && validation.errors.repositorycount ? (
                      <FormFeedback type="invalid">{validation.errors.repositorycount}</FormFeedback>
                    ) : null}
                  </div>
                </Col>
              </Row>
              <Label>Details for your main repositories</Label>
              <div className="table-responsive mb-3">
                <Table bordered>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Local Path</th>
                      <th>Branch</th>
                      <th>Remote (host)</th>
                      <th>Size (GB)</th>
                      <th>Files</th>
                      <th>Clean</th>
                      <th>In OneDrive</th>
                      <th>Last Commit</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {validation.values.repositories.map((row, idx) => (
                      <tr key={idx}>
                        <td><Input type="text" value={row.name} onChange={e => updateRow("repositories", idx, "name", e.target.value)} /></td>
                        <td><Input type="text" value={row.rootpath} onChange={e => updateRow("repositories", idx, "rootpath", e.target.value)} /></td>
                        <td><Input type="text" value={row.branch} onChange={e => updateRow("repositories", idx, "branch", e.target.value)} /></td>
                        <td><Input type="text" value={row.remoteurl} onChange={e => updateRow("repositories", idx, "remoteurl", e.target.value)} /></td>
                        <td><Input type="number" value={row.sizegb} onChange={e => updateRow("repositories", idx, "sizegb", e.target.value)} /></td>
                        <td><Input type="number" value={row.filecount} onChange={e => updateRow("repositories", idx, "filecount", e.target.value)} /></td>
                        <td className="text-center">
                          <Input type="checkbox" checked={row.isclean} onChange={e => updateRow("repositories", idx, "isclean", e.target.checked)} />
                        </td>
                        <td className="text-center">
                          <Input type="checkbox" checked={row.isinsideonedrive} onChange={e => updateRow("repositories", idx, "isinsideonedrive", e.target.checked)} />
                        </td>
                        <td><Input type="text" value={row.lastcommitsummary} onChange={e => updateRow("repositories", idx, "lastcommitsummary", e.target.value)} /></td>
                        <td>
                          {validation.values.repositories.length > 1 && (
                            <Button color="danger" size="sm" onClick={() => removeRow("repositories", idx)}>
                              X
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <Button color="secondary" size="sm" onClick={() => addRow("repositories", { ...emptyRepository })}>
                  + Add Repository
                </Button>
              </div>

              <hr />

              {/* SECTION E - Source Control Coverage */}
              <h5 className="mb-3">E. Source Control Coverage</h5>
              <p className="text-muted">
                Controlled = files inside a Git repository. Uncontrolled = files outside any Git repo.
              </p>
              <Row>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>Controlled file count</Label>
                    <Input name="controlledfilecount" type="number" onChange={validation.handleChange} value={validation.values.controlledfilecount} />
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>Controlled total size (GB)</Label>
                    <Input name="controlledsizegb" type="number" onChange={validation.handleChange} value={validation.values.controlledsizegb} />
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>Uncontrolled file count</Label>
                    <Input name="uncontrolledfilecount" type="number" onChange={validation.handleChange} value={validation.values.uncontrolledfilecount} />
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>Uncontrolled total size (GB)</Label>
                    <Input name="uncontrolledsizegb" type="number" onChange={validation.handleChange} value={validation.values.uncontrolledsizegb} />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>Approx. controlled percentage (0-100)</Label>
                    <Input name="controlledpercentage" type="number" min={0} max={100} onChange={validation.handleChange} value={validation.values.controlledpercentage} />
                  </div>
                </Col>
                <Col md={9}>
                  <div className="mb-3">
                    <Label>Notable uncontrolled areas you're aware of</Label>
                    <Input name="notableuncontrolledareas" type="textarea" onChange={validation.handleChange} value={validation.values.notableuncontrolledareas} />
                  </div>
                </Col>
              </Row>

              <hr />

              {/* SECTION F - Documents */}
              <h5 className="mb-3">F. Documents</h5>
              <Row>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>Number of document files</Label>
                    <Input name="documentfilecount" type="number" onChange={validation.handleChange} value={validation.values.documentfilecount} />
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>Total document size (GB)</Label>
                    <Input name="documentsizegb" type="number" onChange={validation.handleChange} value={validation.values.documentsizegb} />
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>Stored outside Git?</Label>
                    <Input name="documentsoutsidegit" type="select" onChange={validation.handleChange} value={validation.values.documentsoutsidegit}>
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="Not sure">Not sure</option>
                    </Input>
                  </div>
                </Col>
              </Row>
              <div className="mb-3">
                <Label>Where do most documents live?</Label>
                <Input name="documentlocations" type="textarea" onChange={validation.handleChange} value={validation.values.documentlocations} />
              </div>

              <hr />

              {/* SECTION G - Build / Development Artifacts */}
              <h5 className="mb-3">G. Build / Development Artifacts</h5>
              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Estimated total artifact size (GB)</Label>
                    <Input name="artifactsizegb" type="number" onChange={validation.handleChange} value={validation.values.artifactsizegb} />
                  </div>
                </Col>
              </Row>
              <div className="mb-3">
                <Label>Which artifact types are present?</Label>
                <div className="d-flex flex-wrap gap-3">
                  {ARTIFACT_TYPE_OPTIONS.map(opt => (
                    <div className="form-check" key={opt}>
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id={`artifacttype-${opt}`}
                        checked={validation.values.artifacttypes.includes(opt)}
                        onChange={() => toggleCheckbox("artifacttypes", opt)}
                      />
                      <Label className="form-check-label" htmlFor={`artifacttype-${opt}`}>{opt}</Label>
                    </div>
                  ))}
                </div>
              </div>
              <Label>Largest artifact folders you know of</Label>
              <GridSection
                rows={validation.values.artifactfolders}
                columns={[
                  { key: "path", label: "Folder Path", type: "text" },
                  { key: "sizegb", label: "Approx. Size (GB)", type: "number" },
                  { key: "filecount", label: "Approx. File Count", type: "number" },
                  { key: "directorycount", label: "Folder Count", type: "number" },
                ]}
                onChange={(idx, key, value) => updateRow("artifactfolders", idx, key, value)}
                onAdd={() => addRow("artifactfolders", { ...emptyArtifactFolder })}
                onRemove={idx => removeRow("artifactfolders", idx)}
              />

              <hr />

              {/* SECTION H - OneDrive Synchronisation */}
              <h5 className="mb-3">H. OneDrive Synchronisation</h5>
              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>Is any development content synced with OneDrive? *</Label>
                    <Input
                      name="onedrivesynced"
                      type="select"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.onedrivesynced}
                      invalid={!!(validation.touched.onedrivesynced && validation.errors.onedrivesynced)}
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="Not sure">Not sure</option>
                    </Input>
                    {validation.touched.onedrivesynced && validation.errors.onedrivesynced ? (
                      <FormFeedback type="invalid">{validation.errors.onedrivesynced}</FormFeedback>
                    ) : null}
                  </div>
                </Col>
              </Row>
              <div className="mb-3">
                <Label>Which OneDrive account type(s)?</Label>
                <div className="d-flex flex-wrap gap-3">
                  {ONEDRIVE_ACCOUNT_OPTIONS.map(opt => (
                    <div className="form-check" key={opt}>
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id={`onedriveaccount-${opt}`}
                        checked={validation.values.onedriveaccounttypes.includes(opt)}
                        onChange={() => toggleCheckbox("onedriveaccounttypes", opt)}
                      />
                      <Label className="form-check-label" htmlFor={`onedriveaccount-${opt}`}>{opt}</Label>
                    </div>
                  ))}
                </div>
              </div>
              <Label>Details per OneDrive location</Label>
              <div className="table-responsive mb-3">
                <Table bordered>
                  <thead>
                    <tr>
                      <th>Root Path</th>
                      <th>Account</th>
                      <th>Size (GB)</th>
                      <th>Files</th>
                      <th>Has Git Repos</th>
                      <th>Content Types</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {validation.values.onedrivelocations.map((row, idx) => (
                      <tr key={idx}>
                        <td><Input type="text" value={row.rootpath} onChange={e => updateRow("onedrivelocations", idx, "rootpath", e.target.value)} /></td>
                        <td><Input type="text" value={row.account} onChange={e => updateRow("onedrivelocations", idx, "account", e.target.value)} /></td>
                        <td><Input type="number" value={row.sizegb} onChange={e => updateRow("onedrivelocations", idx, "sizegb", e.target.value)} /></td>
                        <td><Input type="number" value={row.filecount} onChange={e => updateRow("onedrivelocations", idx, "filecount", e.target.value)} /></td>
                        <td className="text-center">
                          <Input type="checkbox" checked={row.hasgitrepos} onChange={e => updateRow("onedrivelocations", idx, "hasgitrepos", e.target.checked)} />
                        </td>
                        <td><Input type="text" value={row.contenttypes} onChange={e => updateRow("onedrivelocations", idx, "contenttypes", e.target.value)} /></td>
                        <td>
                          {validation.values.onedrivelocations.length > 1 && (
                            <Button color="danger" size="sm" onClick={() => removeRow("onedrivelocations", idx)}>X</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <Button color="secondary" size="sm" onClick={() => addRow("onedrivelocations", { ...emptyOneDriveLocation })}>
                  + Add OneDrive Location
                </Button>
              </div>
              <div className="mb-3">
                <Label>Any Git repositories located inside OneDrive?</Label>
                <Input name="onedrivegitrepos" type="textarea" onChange={validation.handleChange} value={validation.values.onedrivegitrepos} />
              </div>

              <hr />

              {/* SECTION I - Largest Consumers */}
              <h5 className="mb-3">I. Largest Consumers</h5>
              <Label>Top folders by disk space</Label>
              <GridSection
                rows={validation.values.largestfolders}
                columns={[
                  { key: "path", label: "Folder Path", type: "text" },
                  { key: "sizegb", label: "Approx. Size (GB)", type: "number" },
                ]}
                onChange={(idx, key, value) => updateRow("largestfolders", idx, key, value)}
                onAdd={() => addRow("largestfolders", { ...emptyLargestFolder })}
                onRemove={idx => removeRow("largestfolders", idx)}
              />
              <Label className="mt-3">Largest individual files you're aware of</Label>
              <GridSection
                rows={validation.values.largestfiles}
                columns={[
                  { key: "path", label: "File Path", type: "text" },
                  { key: "sizegb", label: "Approx. Size (GB)", type: "number" },
                  { key: "filetype", label: "Type", type: "text" },
                ]}
                onChange={(idx, key, value) => updateRow("largestfiles", idx, key, value)}
                onAdd={() => addRow("largestfiles", { ...emptyLargestFile })}
                onRemove={idx => removeRow("largestfiles", idx)}
              />

              <hr />

              {/* SECTION J - Notes & Observations */}
              <h5 className="mb-3">J. Notes &amp; Observations</h5>
              <div className="mb-3">
                <Label>Anything that limited this summary?</Label>
                <Input name="limitingnotes" type="textarea" onChange={validation.handleChange} value={validation.values.limitingnotes} />
              </div>
              <div className="mb-3">
                <Label>Any storage concerns or cleanup already planned?</Label>
                <Input name="storageconcerns" type="textarea" onChange={validation.handleChange} value={validation.values.storageconcerns} />
              </div>

              <div className="text-end">
                <Button type="submit" color="primary">
                  Save Workspace Audit
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h5 className="mb-0">Recent Submissions</h5>
          </CardHeader>
          <CardBody>
            <TableContainer ref={tableRef} />
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

const GridSection = ({ rows, columns, onChange, onAdd, onRemove }) => (
  <div className="table-responsive mb-3">
    <Table bordered>
      <thead>
        <tr>
          {columns.map(col => (
            <th key={col.key}>{col.label}</th>
          ))}
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={idx}>
            {columns.map(col => (
              <td key={col.key}>
                <Input
                  type={col.type}
                  value={row[col.key]}
                  onChange={e => onChange(idx, col.key, e.target.value)}
                />
              </td>
            ))}
            <td>
              {rows.length > 1 && (
                <Button color="danger" size="sm" onClick={() => onRemove(idx)}>
                  X
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
    <Button color="secondary" size="sm" onClick={onAdd}>
      + Add Row
    </Button>
  </div>
)

export default Index
